import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { Audit } from '../models/audit/audit';

import { ConfigService } from './config.service';

/**
 * Service for audit-related API calls.
 * On save failure, the API returns an error body with message, reason and statusCode; use {@link getApiErrorDisplayText} to display it.
 */
@Injectable({
  providedIn: 'root'
})
export class AuditService {
  private http = inject(HttpClient);
  configService = inject(ConfigService);

  /**
   * Fetches audits by auditor area.
   * @param area - Auditor identifier.
   * @param all - If true, fetches all audits for the area.
   * @returns Observable of audit list.
   */
  getData(area: string, all: boolean): Observable<Audit[]> {
    let endpointName = `/audit/${area}/`;
    if (all) {
      endpointName += 'all/';
    }
    return this.http.get<Audit[]>(this.configService.getConfiguration().apiHost + '/api' + endpointName);
  }

  /**
   * Fetches a single audit by id.
   * @param id - Audit id.
   * @returns Observable of the audit.
   */
  getDataById(id: string): Observable<Audit> {
    return this.http.get<Audit>(this.configService.getConfiguration().apiHost + '/api/auditbyid/' + id);
  }

  /**
   * Saves or updates an audit. On failure the API returns an error object (message, reason, statusCode).
   * @param audit - Audit to save.
   * @returns Observable with audit and optional emailWarnings (camelCase from API).
   */
  postAudit(audit: Audit): Observable<{ audit: Audit; emailWarnings?: string[] }> {
    return this.http.post<{ audit: Audit; emailWarnings?: string[] }>(
      `${this.configService.getConfiguration().apiHost}/api/audit/${audit.id}`,
      audit
    );
  }

  /**
   * Updates audit status and related fields.
   * @param auditId - Audit id.
   * @param status - New status.
   * @param auditor - Auditor value.
   * @param department - Department value.
   * @param assessmentCoordinator - Assessment coordinator value.
   * @returns Observable of the updated audit.
   */
  setStatus(
    auditId: string,
    status: string,
    auditor: string,
    department: string,
    assessmentCoordinator: string
  ): Observable<Audit> {
    return this.http.post<Audit>(`${this.configService.getConfiguration().apiHost}/api/audit/${auditId}`, {
      id: auditId,
      status,
      auditor,
      department,
      assessmentCoordinator
    });
  }
}
